import { persistentAtom } from '@nanostores/persistent';
import type { IRegionSelectionData } from '@bjb/bjb-web-components/dist/components/jb-region-select-overlay/jb-region-select-overlay.custom';
import { getCookie, setCookie } from './cookie';

// Geo *detection* — where the visitor is right now, resolved server-side from their IP and
// fetched lazily from the geo-data endpoint. Cached client-side only; never sent to the server.
export const $region = persistentAtom<string>('i18n.region', '');
export const $country = persistentAtom<string>('i18n.country', '');

// The market we consider the visitor's — the last market page they visited, the market they chose,
// or their location's market. It is what makes global pages, which carry no market of their own,
// market-aware. A market landing here is never written back to the choice cookie below, so only a
// real choice steers the server-side apex redirect.
export const $market = persistentAtom<string>('i18n.market', '');

// When that market was last confirmed as the visitor's, in epoch milliseconds. The memory lasts as
// long as the choice cookie does, so a visitor who has moved on is placed by their location again
// instead of keeping a market they left behind; every page of that market, and every choice, starts
// the term over. A market without a timestamp counts as expired — re-resolving costs one geo lookup
// and the cookie, the page or that lookup fills the store again right away.
export const $marketRememberedAt = persistentAtom<string>('i18n.marketRememberedAt', '');
const MARKET_MEMORY_MS = 182 * 24 * 60 * 60 * 1000; // the setCookie() default in cookie.ts, in ms

export function rememberMarket(market: string): void {
  $market.set(market);
  $marketRememberedAt.set(market ? String(Date.now()) : '');
}

// Forgets an expired market on the way out, so a stale one is dropped rather than carried around.
function getRememberedMarket(): string {
  const market = $market.get();
  if (!market) {
    return '';
  }
  const rememberedAt = parseInt($marketRememberedAt.get(), 10);
  if (Number.isNaN(rememberedAt) || Date.now() - rememberedAt > MARKET_MEMORY_MS) {
    rememberMarket('');
    return '';
  }
  return market;
}

// The visitor's explicit market + language choice lives in a single cookie (format
// `<marketUid>|<languageIso2>`) so the server can honour it for the apex redirect. It is the
// single source of truth for the chosen market — deliberately not mirrored into localStorage,
// so the two stores cannot drift. Kept in sync with I18nChoiceCookieService.php on the server.
const CHOICE_COOKIE = 'i18nChoice';

function getChosenMarket(): string {
  return (getCookie(CHOICE_COOKIE) ?? '').split('|')[0] ?? '';
}

function setChoice(market: string, languageIso2: string): void {
  setCookie(CHOICE_COOKIE, `${market || ''}|${languageIso2 || ''}`);
}

// The page's own market. The global site carries `data-market` "0", which is not a market uid.
function getPageMarket(): string {
  const pageMarket = document.body.dataset.market ?? '';
  return pageMarket === '0' ? '' : pageMarket;
}

// A market the visitor chose counts as theirs as well, including one chosen before this store
// existed, whose only trace is the cookie. Seeding it here — rather than only reading it in
// getMarket() — keeps the geo lookup from taking such a visitor for one with no market at all and
// filling the store with their location instead.
if (!getRememberedMarket() && getChosenMarket() !== '') {
  rememberMarket(getChosenMarket());
}

// Arriving on a market page makes that market the visitor's; global pages leave it untouched.
if (getPageMarket() !== '') {
  rememberMarket(getPageMarket());
}

// Editor preview (`?enforceI18nGeo`): stand in for geo detection with a country inside the
// previewed page's own market, so the region indicator and the region overlay's preselection
// follow the page rather than wherever the editor happens to sit. The market needs nothing here —
// the page carries its own `data-market`, which rememberMarket() above has already picked up. The
// choice cookie stays untouched: previewing a page is not the editor choosing that market for
// their own browsing.
const enforceCountry = document.body.dataset.enforceI18nCountry;
if (enforceCountry !== undefined) {
  $country.set(enforceCountry);
  $region.set(document.body.dataset.enforceI18nRegion ?? '0');
}

let geoFetch: Promise<void> | null = null;
const spoofIP = new URLSearchParams(window.location.search).get('spoofIP');

function geoDataUrl(): string | null {
  const endpoint = document.body.dataset.geoDataEndpoint;
  if (!endpoint) return null;
  return spoofIP ? `${endpoint}?spoofIP=${encodeURIComponent(spoofIP)}` : endpoint;
}

function fetchGeoData(): Promise<void> {
  if (!geoFetch) {
    const url = geoDataUrl();
    geoFetch = (url ? fetch(url) : Promise.reject(new Error('no endpoint')))
      .then((r) => r.json())
      .then(({ region, country, market }: { region: string; country: string; market: string }) => {
        $region.set(region || '');
        $country.set(country || '');
        // The location only fills in a market we don't have yet; a spoofed IP does overwrite.
        if (market && (!getRememberedMarket() || spoofIP)) {
          rememberMarket(market);
        }
      })
      .catch(() => {});
  }
  return geoFetch;
}

export async function getCountry(): Promise<string> {
  if (!$country.get() || spoofIP) await fetchGeoData();
  return $country.get();
}

export async function getLanguage(): Promise<string> {
  return document.body.dataset.languageUid ?? '';
}

export async function getLanguageIso2(): Promise<string> {
  return document.body.dataset.languageIso2 ?? '';
}

// Page market first, so market pages always reflect themselves; then what we remember for the
// visitor (the cookie backs the atom up, surviving a cleared localStorage); then their location.
// The choice cookie is only read, never written, so it stays the apex redirect's own input.
export async function getMarket(): Promise<string> {
  if (getPageMarket() !== '') {
    return getPageMarket();
  }
  const rememberedMarket = spoofIP ? '' : getRememberedMarket() || getChosenMarket();
  if (rememberedMarket) {
    return rememberedMarket;
  }
  await fetchGeoData();
  // With no market at all the visitor stays global, which is what data-market "0" says.
  return getRememberedMarket() || document.body.dataset.market || '';
}

export async function getRegion(): Promise<string> {
  if (!$region.get() || spoofIP) await fetchGeoData();
  return $region.get();
}

export function setI18nUserSelection(data: IRegionSelectionData): void {
  setChoice(data.market, data.languageIso2);
  rememberMarket(data.market || '');
  $region.set(data.region || '');
  $country.set(data.country || '');
}

// Persist a language switch made in the cookie-consent overlay as the visitor's preferred
// language, keeping their previously chosen market untouched.
export function setChosenLanguage(languageIso2: string): void {
  setChoice(getChosenMarket(), languageIso2);
}
